import { useMonitorStore, MonitoredSession, PacketEvent } from './monitorStore';

type DashboardMessage =
  | { type: 'snapshot'; sessions: MonitoredSession[] }
  | { type: 'session_update'; session: MonitoredSession }
  | { type: 'session_remove'; sessionId: string }
  | { type: 'packet'; event: PacketEvent };

export class MonitorClient {
  private ws: WebSocket | null = null;
  private reconnectTimer: number | null = null;
  private reconnectDelay = 1000;
  private maxReconnectDelay = 30000;

  connect(): void {
    const token = useMonitorStore.getState().token;
    if (!token) {
      console.error('No auth token available');
      return;
    }

    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    useMonitorStore.getState().setConnectionStatus('connecting');

    const wsUrl = `wss://api.caldera.homes/monitor/ws?token=${encodeURIComponent(token)}`;

    this.ws = new WebSocket(wsUrl);

    this.ws.onopen = () => {
      console.log('Monitor WebSocket connected');
      useMonitorStore.getState().setConnectionStatus('connected');
      this.reconnectDelay = 1000; // Reset delay on successful connection
    };

    this.ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data) as DashboardMessage;
        this.handleMessage(msg);
      } catch (e) {
        console.error('Failed to parse WebSocket message:', e);
      }
    };

    this.ws.onclose = (event) => {
      console.log('Monitor WebSocket closed:', event.code, event.reason);
      useMonitorStore.getState().setConnectionStatus('disconnected');

      // Auth failure - don't reconnect
      if (event.code === 4001 || event.code === 4003) {
        useMonitorStore.getState().clearAuth();
        return;
      }

      this.scheduleReconnect();
    };

    this.ws.onerror = (event) => {
      console.error('Monitor WebSocket error:', event);
    };
  }

  disconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    useMonitorStore.getState().setConnectionStatus('disconnected');
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;

    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, this.reconnectDelay);

    // Exponential backoff
    this.reconnectDelay = Math.min(
      this.reconnectDelay * 2,
      this.maxReconnectDelay
    );
  }

  private handleMessage(msg: DashboardMessage): void {
    const store = useMonitorStore.getState();

    switch (msg.type) {
      case 'snapshot':
        store.setSnapshot(msg.sessions);
        break;
      case 'session_update':
        store.updateSession(msg.session);
        break;
      case 'session_remove':
        store.removeSession(msg.sessionId);
        break;
      case 'packet':
        console.log('[MonitorClient] packet:', msg.event.direction, msg.event.targetType);
        store.addPacket(msg.event);
        break;
    }
  }
}

// Singleton instance
export const monitorClient = new MonitorClient();
